import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AmplitudeService } from '@inyova/inyova-shared';
import {
  Customer,
  CustomerAccount,
  IbanOption,
  WithdrawalAllocation,
  WithdrawalForm,
  WithdrawalReasons,
  WithdrawalStatus,
  WithdrawalSteps,
  WithdrawRequest,
  WithdrawType
} from '@inyova/models';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { WithdrawalService } from '../../services/withdrawal.service';

@Component({
  selector: 'app-withdrawal',
  templateUrl: './withdrawal.component.html',
  styleUrls: ['./withdrawal.component.scss'],
  standalone: false
})
export class WithdrawalComponent implements OnInit, OnDestroy {
  @Input() accountInvested = true;
  @Input() withdrawalAllocation: WithdrawalAllocation;
  @Input() currentAccount: CustomerAccount;
  @Input() hasWithdrawalPromotion: boolean;
  @Input() customer: Customer;
  @Input() isMobileApp = false;
  @Output() reFetchPromotions: EventEmitter<void> = new EventEmitter<void>();
  @Output() getWithdrawalData: EventEmitter<void> = new EventEmitter<void>();
  @Output() trackActivity: EventEmitter<string> = new EventEmitter<string>();
  @Output() showToastError: EventEmitter<string> = new EventEmitter<string>();
  @Output() scrollToTop: EventEmitter<void> = new EventEmitter<void>();
  @Output() navigateTo: EventEmitter<string> = new EventEmitter<string>();

  protected readonly onDestroy$ = new Subject<void>();

  currentWdlStep: WithdrawalSteps;
  ibanList: IbanOption[] = [];
  reasons: WithdrawalReasons;
  isLoading = false;
  stepsHistory: WithdrawalSteps[] = [];
  withdrawalSteps = WithdrawalSteps;
  withdrawalForm: FormGroup;
  withdrawInProcess = false;
  // this.withdrawalMinBuffer represents 5% buffer value which has been subtracted from the minimumAmount
  // the purpose of this buffer is to protect ourselves from daily market changes
  withdrawalMinBuffer = 0.05;
  withdrawalStatus: WithdrawalStatus;

  get minInvestmentValue(): number {
    return this.currentAccount.minimum_investment;
  }

  get withdrawalGroup(): FormGroup {
    return this.withdrawalForm.get('withdrawal') as FormGroup;
  }

  get ibanGroup(): FormGroup {
    return this.withdrawalForm.get('iban') as FormGroup;
  }

  get extraGroup(): FormGroup {
    return this.withdrawalForm.get('extra') as FormGroup;
  }

  constructor(
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
    private withdrawalService: WithdrawalService,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit(): void {
    this.withdrawalForm = this.initializeForm();

    this.withdrawalGroup
      .get('amount')
      .valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((value) => {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        this.trackActivity.emit(`[Input] New Withdrawal: ${!value ? 0 : value}`);
      });

    this.ibanGroup
      .get('newIban')
      .valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (typeof value !== 'undefined' && value !== null && (value as string).length > 0) {
          const trimmed = (value as string).replace(/\s/g, '');
          this.ibanGroup.get('newIban').setValue(trimmed);
          this.ibanGroup.get('newIban').updateValueAndValidity();
          this.cd.markForCheck();
        }
      });

    this.withdrawalService.checkWithdrawalInProgress().subscribe(
      ({ data }) => {
        if (data.length) {
          this.currentWdlStep = WithdrawalSteps.STATUS;
          this.withdrawalStatus = 'processing';
          this.withdrawInProcess = true;
          this.cd.markForCheck();
          return;
        }

        this.withdrawInProcess = false;
        this.currentWdlStep = WithdrawalSteps.INITIAL;
        this.amplitudeService.trackEvent('Withdrawal Started', {
          eligibleForPromotion: !this.hasWithdrawalPromotion
        });
        this.fetchData();
        this.cd.markForCheck();
      },
      () => {
        this.withdrawInProcess = false;
        this.showToastError.emit(this.isMobileApp ? 'SHARED.errors.something' : 'personal.flash.something');
        if (this.isMobileApp) {
          this.navigateTo.emit('/tabs/account');
        }
      }
    );
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  initializeForm(): FormGroup {
    return this.fb.group({
      withdrawal: this.fb.group({
        type: ['', Validators.required],
        amount: ''
      }),
      iban: this.fb.group({
        type: ['', Validators.required],
        iban: '',
        newIban: ''
      }),
      extra: this.fb.group({
        reasons: [[], [Validators.required]],
        keepAccount: [true]
      })
    });
  }

  fetchData() {
    this.getWithdrawalData.emit();
    this.withdrawalService.getIBANList().subscribe((response) => {
      this.ibanList = response.data.map((item) => ({
        title: item.attributes.bank,
        desc: item.attributes.iban,
        type: 'account',
        value: item.attributes.iban
      }));
    });

    this.withdrawalService.getWithdrawalReasonOptions().subscribe({
      next: ({ survey_questions }) => {
        const [reasons] = survey_questions;
        this.reasons = reasons;
      },
      error: () => {
        this.reasons = { id: 0, inyova_id: 0, survey_answer_options: [], question_text: '' };
      }
    });
  }

  onIbanCheck(iban: string) {
    this.trackActivity.emit(`[Button] New Withdrawal: Check new IBAN`);
    this.withdrawalService.checkCustomerIBAN({ iban }).subscribe(
      (response) => {
        if (response['errors']) {
          this.ibanGroup.get('newIban').setErrors({ invalidIban: true });
          this.cd.markForCheck();
          return;
        }
        this.onNextStep(WithdrawalSteps.REASONS);
        this.cd.markForCheck();
      },
      () => {
        this.showToastError.emit(this.isMobileApp ? 'SHARED.errors.something' : 'personal.flash.something');
      }
    );
  }

  onOpenStep(step: WithdrawalSteps) {
    this.trackActivity.emit(`[Button] New Withdrawal: Go to ${step} step`);
    this.currentWdlStep = step;
    const stepIndex = this.stepsHistory.indexOf(step);
    this.stepsHistory.splice(stepIndex);
  }

  onOpenProfile() {
    this.trackActivity.emit(`[Button] New Withdrawal: Back to profile`);

    if (this.isMobileApp) {
      this.navigateTo.emit('/tabs/account');
    }
  }

  onPrevStep() {
    this.currentWdlStep = this.stepsHistory.pop();
    this.trackActivity.emit(`[Button] New Withdrawal: Back to ${this.currentWdlStep}`);
  }

  onNextStep(next: WithdrawalSteps) {
    const { event, data } = this.getAmplitudeData(this.currentWdlStep, next);
    this.amplitudeService.trackEvent(event, data);
    this.trackActivity.emit(`[Button] New Withdrawal: Continue to ${next}`);
    const excludedSteps: WithdrawalSteps[] = [
      WithdrawalSteps.UPDATE_NOTIFICATION,
      WithdrawalSteps.IMPACT_NOTIFICATION,
      WithdrawalSteps.PROMO_NOTIFICATION
    ];

    if (!excludedSteps.includes(next)) {
      const shouldPushInitial =
        (this.hasWithdrawalPromotion && this.currentWdlStep === WithdrawalSteps.IMPACT_NOTIFICATION) ||
        (this.withdrawalGroup.get('type').value === WithdrawType.PARTIAL && this.currentWdlStep === WithdrawalSteps.IMPACT_NOTIFICATION) ||
        this.currentWdlStep === WithdrawalSteps.PROMO_NOTIFICATION;

      this.stepsHistory.push(shouldPushInitial ? WithdrawalSteps.INITIAL : this.currentWdlStep);
    }
    this.currentWdlStep = next;
    this.scrollToTop.emit();
  }

  onActivatePromotion() {
    this.trackActivity.emit(`[Button] Accept free 3 months`);
    this.amplitudeService.trackEvent('Withdrawal Promotion Request Submitted');
    this.withdrawalService.activateWithdrawalPromotion().subscribe({
      next: () => {
        this.withdrawalStatus = 'promotion';
        this.currentWdlStep = WithdrawalSteps.STATUS;
        this.reFetchPromotions.emit();
        this.amplitudeService.trackEvent('Withdrawal Promotion Activated');
        this.cd.markForCheck();
      },
      error: () => {
        this.withdrawalStatus = 'fail';
        this.currentWdlStep = WithdrawalSteps.STATUS;
        this.cd.markForCheck();
      }
    });
  }

  onCancelWithdrawal(currentWdlStep: WithdrawalSteps) {
    this.trackActivity.emit(`[Button] Stay with Inyova: From step ${currentWdlStep}`);
    this.withdrawalStatus = 'canceled';
    this.currentWdlStep = WithdrawalSteps.STATUS;
  }

  onSubmitRequest() {
    this.trackActivity.emit(`[Button] New Withdrawal: Submit request`);
    this.amplitudeService.trackEvent('Withdrawal Submitted');
    this.isLoading = true;
    this.withdrawalService.sendWithdrawalRequest(this.getRequestData(this.withdrawalForm.value, this.currentAccount.id, this.reasons.id)).subscribe(
      () => {
        this.isLoading = false;
        this.withdrawalStatus = 'success';
        this.currentWdlStep = WithdrawalSteps.STATUS;

        this.cd.markForCheck();
      },
      () => {
        this.isLoading = false;
        this.withdrawalStatus = 'fail';
        this.currentWdlStep = WithdrawalSteps.STATUS;
        this.cd.markForCheck();
      }
    );
  }

  getRequestData(value: WithdrawalForm, accountId: string, surveyId: number): WithdrawRequest {
    return <WithdrawRequest>{
      type: 'new_funds_withdrawal_request',
      attributes: {
        type: value.withdrawal.type,
        iban: value.iban.type === 'new' ? value.iban.newIban : value.iban.iban,
        close_account: value.withdrawal.type === WithdrawType.FULL ? value.extra.keepAccount : null,
        amount_to_deduct: value.withdrawal.amount,
        new_reference_account_requested: value.iban.type === 'new',
        account_id: accountId,
        survey_answers: {
          question_id: surveyId,
          answer: value.extra.reasons
        }
      }
    };
  }

  getAmplitudeData(currentStep: WithdrawalSteps, next: WithdrawalSteps): { event: string; data: any } {
    switch (currentStep) {
      case WithdrawalSteps.INITIAL:
        return {
          event: 'Withdrawal Type Selected',
          data: { type: this.withdrawalGroup.get('type').value }
        };
      case WithdrawalSteps.UPDATE_NOTIFICATION:
        return {
          event: 'Withdrawal Update Step Selected',
          data: { selectedOption: next === WithdrawalSteps.IMPACT_NOTIFICATION ? 'Continue Withdrawal' : 'Stay with Inyova' }
        };
      case WithdrawalSteps.IMPACT_NOTIFICATION:
        return {
          event: 'Withdrawal Impact Step Selected',
          data: {
            selectedOption: next === WithdrawalSteps.IBAN || next === WithdrawalSteps.PROMO_NOTIFICATION ? 'Continue Withdrawal' : 'Stay with Inyova'
          }
        };
      case WithdrawalSteps.PROMO_NOTIFICATION:
        return {
          event: 'Withdrawal Promo Step Selected',
          data: { selectedOption: next === WithdrawalSteps.IBAN ? 'Continue Withdrawal' : 'Accept free 3 months' }
        };
      case WithdrawalSteps.IBAN:
        return {
          event: 'Withdrawal Bank Account Selected',
          data: {}
        };
      case WithdrawalSteps.REASONS:
        return {
          event: 'Withdrawal Survey Submitted',
          data: {}
        };
      case WithdrawalSteps.CLOSE:
        return {
          event: 'Account Status After Withdrawals Selected',
          data: { selectedOption: this.extraGroup.get('keepAccount').value ? 'close_account' : 'keep_account' }
        };
    }
  }

  protected readonly Object = Object;
}
